import classNames from 'classnames';
import { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ConfigContext } from 'palette/Config';
import { ModalContext } from 'palette/Modal';
import { Svg } from 'palette/Svg';
import { BodyText, Header } from 'palette/Typography';
import { getDealsActivated } from '@common/ActivateDeals/actions';
import ActivateDealsTag from '@common/ActivateDeals/components/ActivateDealsTag';
import ActivateDealsTermsModal from '@common/ActivateDeals/components/ActivateDealsTermsModal';
import { useActiveDeal } from '@common/ActivateDeals/hooks';
import { useTranslate } from '@common/Translations';
import { get as getCookie, set as setCookie } from '@helpers/cookie';
import { MODAL_ID } from '../constants';
import ActivateDealsButton from './ActivateDealsButton';
import '../styles/ActivateDeals.scss';

function ActivateDeals({
    isServerSide = false,
    showArrow = false,
    showTag = false,
    showIcon = false,
    selectedEanID,
    customClass,
    isServerBooztletListing = false,
    isHidden
}) {
    const translate = useTranslate();
    const { setActiveModalId } = useContext(ModalContext);
    const [isModalActive, setIsModalActive] = useState(false);
    const {
        activated,
        modifier,
        prices = null,
        env,
        shouldHideMDV = false,
        mdvInfoMessage = null
    } = useSelector(({ activateDeals = {} }) => activateDeals);
    const deal = useActiveDeal();
    const hasDeals = deal !== null;
    const isActivated = activated.length > 0;
    const isBooztletPDP = env === 'shopbooztlet' && modifier === 'pdp';
    const eanHasMDV = prices?.[selectedEanID] !== 'undefined';
    const dispatch = useDispatch();
    const { svgPath } = useContext(ConfigContext);

    const showTerms = (e) => {
        e.preventDefault();
        setIsModalActive(true);
        setActiveModalId(MODAL_ID);
    };

    const handleOnClose = () => {
        setActiveModalId(null);
    };

    useEffect(() => {
        if (modifier === 'listing' && !isServerSide) {
            const listing = document.querySelector('.product-list--deals, .listing-products--deals');

            if (listing) {
                if (!isActivated) {
                    listing.classList.remove('product-list--deals--active');
                } else {
                    listing.classList.add('product-list--deals--active');
                }
            }

            document
                .querySelectorAll('.p-card__activate-deals-tag')
                .forEach((tag) => {
                    const currentPrice = tag.parentNode.querySelector('.p-card__price__activated')
                    || tag.parentNode.parentNode.querySelector('.p-card__price__activated');

                    if (currentPrice) {
                        currentPrice.innerHTML = tag.dataset.price;
                    }

                    // adjust styles from recommended price label
                    const recommendedPriceTag = tag.parentNode.querySelector('.p-card__price--previous');

                    if (recommendedPriceTag) {
                        recommendedPriceTag.style.textDecoration = 'none';
                        recommendedPriceTag.children[0].style.marginLeft = '0px';
                    }
                });

            // remove outline from desktop listings
            document.querySelectorAll('.p-card__price__previous').forEach((elem) => {
                if (elem) {
                    elem.classList.add('p-card__price__no-line-though');
                }
            });
        }
    }, [activated, modifier, isServerSide, isActivated]);

    useEffect(() => {
        if (!isServerSide) {
            const searchQuery = new URLSearchParams(window.location.search);

            dispatch(getDealsActivated({ volume: searchQuery.get('volume') ?? 'null' }));

            if (!getCookie('bzt_volume_cache') && shouldHideMDV) {
                setCookie('bzt_volume', '1', { expires: 1 });
                setCookie('bzt_volume_cache', '1', { expires: 1 });
            }
        }
    }, [isServerSide, dispatch, shouldHideMDV]);

    if (hasDeals && !shouldHideMDV) {
        return (
            <>
                {isBooztletPDP && !isActivated && eanHasMDV
                    && (
                        <div
                            className="activate-deals__booztlet__tag"
                            style={{ backgroundColor: deal?.banner_color, color: deal?.text_color }}
                        >
                            <BodyText
                                className="activate-deals__text"
                            >
                                {prices?.[selectedEanID]?.tag}
                            </BodyText>
                        </div>
                    )}
                <div
                    className={classNames(
                        'activate-deals-container',
                        { [`activate-deals-container--${modifier}`]: modifier },

                        customClass
                    )}
                >
                    {showTag && prices !== null && (
                        <ActivateDealsTag prices={prices} />
                    )}
                    <div
                        className={classNames('activate-deals', {
                            'activate-deals__booztlet-listing': isServerBooztletListing,
                            'activate-deals__booztlet-listing__hidden': isHidden
                        })}
                        style={{ backgroundColor: deal?.banner_color, color: deal?.text_color }}
                    >
                        {(isBooztletPDP && eanHasMDV)
                            && (
                                <div
                                    className="activate-deals__booztlet__top-triangle"
                                    style={{ borderBottom: `16px solid ${deal?.banner_color}` }}
                                />
                            )}
                        {showArrow && <span className="activate-deals__arrow" />}
                        {showIcon && (
                            <div className="activate-deals__icon">
                                <Svg
                                    name="voucher-tag-icon"
                                    svgUrl={svgPath}
                                />
                            </div>
                        )}
                        <div
                            className="activate-deals__info"
                        >
                            <Header
                                className="activate-deals__text"
                                level="6"
                            >
                                {deal.name}
                            </Header>
                            <BodyText
                                className="activate-deals__text"
                            >
                                {/* eslint-disable-next-line react/no-danger */}
                                <span dangerouslySetInnerHTML={{ __html: mdvInfoMessage || deal.info }} />
                            </BodyText>
                            {' | '}
                            <button
                                type="button"
                                onClick={showTerms}
                            >
                                <BodyText
                                    className="activate-deals__text"
                                >
                                    {translate('mdv.terms_and_conditions')}
                                </BodyText>
                            </button>
                        </div>
                        <ActivateDealsButton />
                    </div>
                    <ActivateDealsTermsModal
                        visible={isModalActive}
                        onClose={handleOnClose}
                    />
                </div>
            </>
        );
    }

    return null;
}

export default ActivateDeals;
